import { useMemo } from 'react';
import { defineMessages } from 'react-intl';
import LazyLoad from 'react-lazyload';
import { string, number } from 'prop-types';
import AppStore from '@alltrails/shared/components/storeBadges/AppStore';
import GooglePlay from '@alltrails/shared/components/storeBadges/GooglePlay';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import starRating from './star_rating.png';
import { toATLocalString } from '../../../utils/number_util';
import * as styles from './styles/styles.module.scss';
import { DESKTOP_BREAKPOINT, MEDIA_QUERY_SMALL_DOWN } from '../../../utils/constants/breakpoints';

const EDITORS_CHOICE_STRINGS = defineMessages({
  INSPIRATION_FOR: {
    defaultMessage: 'Inspiration for wherever'
  },
  YOUR_TRAIL: {
    defaultMessage: 'your trail leads'
  },
  EDITORS_CHOICE: {
    defaultMessage: 'Recipient of the App Store Editors’ Choice award'
  },
  BG_IMAGE_ALT: {
    defaultMessage: 'People hiking up a hill next to the ocean'
  }
});

type Props = {
  languageRegionCode: LanguageRegionCode;
  windowWidth: number;
};

const BG_IMAGE_BASE_URL = 'https://cdn-assets.alltrails.com/assets/images/homepage/editors_choice'

const EditorsChoiceBlock = ({ languageRegionCode, windowWidth }: Props): JSX.Element => {
  const {
    formattedDefaultMessages: { INSPIRATION_FOR, YOUR_TRAIL, EDITORS_CHOICE, BG_IMAGE_ALT }
  } = useFormatMessage(EDITORS_CHOICE_STRINGS);
  const isDesktop = useMemo(() => {
    return windowWidth && windowWidth >= DESKTOP_BREAKPOINT;
  }, [windowWidth]);

  const [appStoreWidth, googlePlayWidth] = useMemo(() => {
    return [
      isDesktop ? 144 : 118,
      isDesktop ? 162 : 133
    ];
  }, [isDesktop]);

  const desktopBgImageUrl = `${BG_IMAGE_BASE_URL}/background-desktop`
  const mobileBgImageUrl = `${BG_IMAGE_BASE_URL}/background-mobile`

  return (
    <div className={styles.container}>
      <picture>
        <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={`${mobileBgImageUrl}.avif`} type="image/avif" />
        <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={`${mobileBgImageUrl}.webp`} type="image/webp" />
        <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={`${mobileBgImageUrl}.jpg`} type="image/jpg" />
        <source srcSet={`${desktopBgImageUrl}.avif`} type="image/avif" />
        <source srcSet={`${desktopBgImageUrl}.webp`} type="image/webp" />
        <img src={`${desktopBgImageUrl}.jpg`} alt={BG_IMAGE_ALT} className={styles.containerBgImage} loading="lazy" />
      </picture>
      <div className={styles.innerContainer}>
        <div className={styles.badgeStarContainer}>
          <LazyLoad>
            <img
              className={styles.badgeImage}
              src={`https://cdn-assets.alltrails.com/assets/images/homepage/editors_choice/apple-editor-badge-${languageRegionCode}.png`}
              alt={EDITORS_CHOICE}
            />
          </LazyLoad>
          <span className={styles.rating}>{toATLocalString(4.9, languageRegionCode, 1)}</span>
          <LazyLoad>
            <img alt="" src={starRating} className={styles.starsImage} />
          </LazyLoad>
        </div>
        <h3 className={styles.titleTop}>{INSPIRATION_FOR}</h3>
        <h3 className={styles.titleBottom}>{YOUR_TRAIL}</h3>
        <div className={styles.stores}>
          <>
            <LazyLoad>
              <AppStore width={appStoreWidth} languageRegionCode={languageRegionCode} className={`${styles.appStore}`} />
              <GooglePlay width={googlePlayWidth} languageRegionCode={languageRegionCode} />
            </LazyLoad>
          </>
        </div>
      </div>
    </div>
  );
};

EditorsChoiceBlock.propTypes = {
  languageRegionCode: string,
  windowWidth: number
};

EditorsChoiceBlock.defaultProps = {
  languageRegionCode: LanguageRegionCode.English,
  windowWidth: null
};

export default EditorsChoiceBlock;
